import { useState } from "react";
import { Copy, Printer, MessageCircle, Link2, Check } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

interface ResourceShareBlockProps {
  /** Path of the resource page, e.g. "/resources/mot-pass-checklist" */
  path: string;
  /** Short resource title, used in the WhatsApp share message */
  title: string;
  /** Show the Print button (resources designed to be printable). Default true. */
  printable?: boolean;
}

const ORIGIN = "https://woodseerautos.co.uk";

export function ResourceShareBlock({ path, title, printable = true }: ResourceShareBlockProps) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${ORIGIN}${path}`;
  const waMsg = encodeURIComponent(
    `Useful free resource from Woodseer Autos: ${title}\n${fullUrl}`,
  );
  const waUrl = `https://wa.me/?text=${waMsg}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      trackEvent("resource_copy_link", { location: path });
    } catch {
      /* ignore */
    }
  };

  const onPrint = () => {
    trackEvent("resource_print", { location: path });
    if (typeof window !== "undefined") window.print();
  };

  return (
    <section className="no-print py-12 md:py-16 bg-secondary/40 border-y border-border">
      <div className="mx-auto max-w-3xl px-4 md:px-6">
        <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">
          Share this resource
        </p>
        <h2 className="mt-2 text-xl md:text-2xl font-extrabold tracking-tight text-foreground">
          Free to share, print and link to
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          Running a local blog, community website, driving school, fleet page or business resource list?
          You're welcome to link to this free Woodseer Autos guide as a helpful resource for drivers.
        </p>
        <p className="mt-2 text-xs text-muted-foreground italic">
          Suggested attribution: "Source: Woodseer Autos, independent garage and DVSA-authorised MOT
          station in Poplar, East London."
        </p>

        <div className="mt-6 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
          >
            {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
            {copied ? "Link copied" : "Copy page link"}
          </button>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("resource_whatsapp_share", { location: path })}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
          >
            <MessageCircle className="h-4 w-4 text-[#25D366]" />
            Share on WhatsApp
          </a>
          {printable && (
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print this page
            </button>
          )}
          <a
            href="https://linktr.ee/woodseerautos"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("resource_external_share_click", { location: path, target: "linktree" })}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
          >
            <Link2 className="h-4 w-4" />
            All our links
          </a>
        </div>
      </div>
    </section>
  );
}
